import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import arcLogo from "@/assets/arc-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/Layout";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";

export default function Login() {
  const { authenticate } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", form);
      authenticate(data.token, data.user);
      toast.success(`Welcome back, ${data.user.username}!`);
      if (data.user.role === "admin") nav("/admin");
      else if (data.user.status !== "active") nav("/register");
      else nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col p-6 md:p-12">
      <div className="flex items-center justify-between">
        <button onClick={() => nav("/")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Home</button>
        <ThemeToggle />
      </div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
        <div className="flex items-center gap-2 mb-8">
          <img src={arcLogo} alt="Art Review Club" className="h-10 w-10 rounded-xl object-contain" />
          <span className="font-heading text-xl font-bold">Art Review Club</span>
        </div>
        <h1 className="font-heading text-3xl font-bold">Welcome back</h1>
        <p className="text-muted-foreground mt-1">Log in to your AR Club account.</p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label htmlFor="username">Username</Label>
            <Input id="username" data-testid="login-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required className="h-12 rounded-xl mt-1.5" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1.5">
              <Input id="password" data-testid="login-password" type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="h-12 rounded-xl pr-12" />
              <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="login-toggle-password" aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <Button type="submit" disabled={submitting} data-testid="login-submit" className="w-full h-12 rounded-full navy-gradient text-white font-semibold">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log In"}
          </Button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-6">
          New here? <button onClick={() => nav("/register")} className="text-gold font-semibold" data-testid="goto-register">Join AR Club</button>
        </p>
      </motion.div>
    </div>
  );
}
